import { useState } from "react";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    Stack,
    Typography,
    Card,
    CardContent,
    Grid,
    TextField
} from "@mui/material";
import {
    PriceChange as PriceChangeIcon,
    LocalAtm as LocalAtmIcon,
    TrendingUp as TrendingUpIcon,
    TrendingDown as TrendingDownIcon,
    Close as CloseIcon,
    Public as PublicIcon
} from "@mui/icons-material";
import {
    EditButton,
    ShowButton,
    UrlField,
    useNotify,
    useDataProvider,
    useRecordContext,
    useRedirect,
    useGetIdentity,
    useShowContext,
    TextField as AdminTextField,
} from "react-admin";
import { useMutation } from '@tanstack/react-query';

import HelpCenterIcon from '@mui/icons-material/HelpCenter';

import { IrmDataProvider } from '../../providers/types';
import { KEEP_DIALOG_SIZE } from '../../root/touchTheme';
import { Fund, Position, Transaction } from "../../types/types";

interface FundAsideProps {
    link?: string;
    position?: Position;
}

export const FundAside = ({ link = "edit", position }: FundAsideProps) => {
    const record = useRecordContext<Fund>();
    if (!record) return null;

    return (
        <Stack ml={4} width={250} minWidth={250} spacing={2}>
            <Stack direction="row" spacing={1}>
                {link === "edit" ? <EditButton label="Edit Fund" /> : <ShowButton label="Show Fund" />}
            </Stack>

            {position && <PositionInfo position={position} />}
        </Stack>
    );
};


const PositionInfo = ({ position }: { position: Position }) => {
    const { record, isPending } = useShowContext<Fund>();
    const [action, setAction] = useState<"deposit" | "withdraw" | null>(null);
    const [amount, setAmount] = useState<string>("");
    const [dialogOpen, setDialogOpen] = useState(false);
    const { identity } = useGetIdentity();

    const dataProvider = useDataProvider<IrmDataProvider>();
    const notify = useNotify();
    const redirect = useRedirect();

    const currency: "EUR" | "USD" | "CHF" = (localStorage.getItem("currency") as "EUR" | "USD" | "CHF") || "EUR";

    const { mutate, status } = useMutation({
        mutationKey: ['transaction'],
        mutationFn: async () => {
            if (!action || !amount) throw new Error("Invalid transaction data");
            if (!identity?.id) throw new Error("User identity not available");

            const userId = String(identity.id); // Get the actual logged-in user ID

            const transactionData: Partial<Transaction> = {
                user_id: userId,
                transfer_type: action === "deposit" ? "deposit" : "withdrawal",
                source: action === "deposit" ? userId : position.asset_id,
                target: action === "deposit" ? position.asset_id : userId,
                status: "pending",
                asset_id: position.asset_id,
                amount: action === "deposit" ? 0 : parseFloat(amount),
                valuation: { EUR: parseFloat(amount) },
                cost: {},
                cost_valuation: {},
                meta_data: {},
            };

            return dataProvider.createTransaction(transactionData);
        },
        onSuccess: (data) => {
            handleClose();
            notify("Transaction successfully created.");
            redirect(`/transactions/${data.id}/show`);
        },
        onError: () => {
            notify("An error occurred while processing the transaction.", { type: "error" });
        },
    });

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setAmount(e.target.value); // Simply update the state, no checks here
    };

    const handleClose = () => {
        setDialogOpen(false);
        setTimeout(() => {
            setAction(null);
            setAmount("");
        }, 200); // Delay resetting state to avoid flickering
    };

    const handleOpen = () => {
        const numberValue = parseFloat(amount);
        // Perform checks when the dialog is supposed to open
        if (amount === "") {
            notify("Please enter the amount you wish to transact.", { type: "warning" });
            return;
        }
        if (isNaN(numberValue)) {
            notify("The amount entered is not a valid number.", { type: "warning" });
            return;
        }
        if (action === "deposit" && numberValue < 100) {
            notify("The minimum deposit amount is 100 EUR.", { type: "warning" });
            return;
        }
        if (action === "withdraw" && numberValue < 1) {
            notify("The minimum amount for withdrawal is 1 share.", { type: "warning" });
            return;
        }
        if (action === "withdraw" && numberValue > position.size) {
            notify(`The amount of shares requested to withdraw (${numberValue}) exceeds the available position size (${position.size}).`, { type: "warning" });
            return;
        }

        setDialogOpen(true); // Only open the dialog if all checks are passed
    };

    const handleConfirm = () => {
        const numberValue = parseFloat(amount);
        // Repeat validation checks when confirming, in case of any changes or async issues
        if (amount === "") {
            notify("Please enter the amount you wish to transact.", { type: "warning" });
            return;
        }
        if (isNaN(numberValue)) {
            notify("The amount entered is not a valid number.", { type: "warning" });
            return;
        }
        if (action === "deposit" && numberValue < 100) {
            notify("The minimum deposit amount is 100 EUR.", { type: "warning" });
            return;
        }
        if (action === "withdraw" && numberValue < 1) {
            notify("The minimum amount for withdrawal is 1 share.", { type: "warning" });
            return;
        }
        if (action === "withdraw" && numberValue > position.size) {
            notify("Cannot withdraw more shares than available.", { type: "warning" });
            return;
        }

        mutate(); // Proceed with the mutation if all validations pass
    };

    return (
        <Stack>
            <Typography variant="subtitle2">Position</Typography>
            <Divider sx={{ mb: 1 }} />
            <Card>
                <CardContent sx={{ "&:last-child": { paddingBottom: "16px" }, padding: "16px", position: "relative" }}>

                    <Grid container spacing={1}>
                        <Grid item xs={3} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            <PriceChangeIcon fontSize="small" />
                            <Typography variant="caption" sx={{ fontWeight: "bold" }}>Shares</Typography>
                        </Grid>
                        <Grid item xs={9} sx={{ display: "flex", justifyContent: "flex-end" }}>
                            <Typography variant="body2">{position.size.toFixed(2)}</Typography>
                        </Grid>

                        <Grid item xs={3} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            <LocalAtmIcon fontSize="small" />
                            <Typography variant="caption" sx={{ fontWeight: "bold" }}>Value</Typography>
                        </Grid>
                        <Grid item xs={9} sx={{ display: "flex", justifyContent: "flex-end" }}>
                            <Typography variant="body2">
                                {position.value.global[currency]?.toFixed(2) ?? "N/A"} {currency}
                            </Typography>
                        </Grid>

                        <Grid item xs={3} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            {position.unrealized_pnl.global[currency] >= 0 ? <TrendingUpIcon fontSize="small" /> : <TrendingDownIcon fontSize="small" />}
                            <Typography variant="caption" sx={{ fontWeight: "bold" }}>P&L</Typography>
                        </Grid>
                        <Grid item xs={9} sx={{ display: "flex", justifyContent: "flex-end" }}>
                            <Typography variant="body2" color={position.unrealized_pnl.global[currency] >= 0 ? "success.main" : "error.main"}>
                                {position.unrealized_pnl.global[currency]?.toFixed(2) ?? "N/A"} {currency}
                            </Typography>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {action && (
                <Stack spacing={0} sx={{ mt: 2 }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Typography variant="subtitle2">
                            {action === "deposit" ? "Deposit Amount (EUR)" : "Withdraw Shares"}
                        </Typography>
                        <IconButton onClick={() => setAction(null)} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Stack>
                    <TextField
                        sx={{
                            mt: 1, mb: 2,
                            "& .MuiInputBase-input": {
                                padding: "10px", // Adjust padding inside input
                            },
                        }}
                        type="number"
                        value={amount}
                        onChange={handleChange}
                        fullWidth
                        placeholder={action === "deposit" ? "Enter amount in EUR" : "Enter shares to withdraw"}
                        inputProps={{
                            min: "0",  // Minimum value
                            max: action === "withdraw" ? position.size.toString() : undefined,  // Maximum value for withdrawal
                            step: "1"  // Allow decimal values
                        }}
                    />
                    <Button variant="contained" color="primary" fullWidth onClick={handleOpen}>
                        {action === "deposit" ? "Deposit" : "Withdrawal"}
                    </Button>
                </Stack>
            )}

            {/* Deposit & Withdrawal Buttons - Full Width */}
            {!action && (
                <Stack spacing={1} sx={{ mt: 2 }}>
                    <Button variant="contained" color="primary" fullWidth onClick={() => setAction("deposit")}>
                        Deposit
                    </Button>
                    {position.size > 0 && (
                        <Button variant="contained" color="primary" fullWidth onClick={() => setAction("withdraw")}>
                            Withdraw
                        </Button>
                    )}
                </Stack>
            )}

            <Dialog
                open={dialogOpen}
                onClose={handleClose}
                className={KEEP_DIALOG_SIZE}
            >
                <DialogTitle>
                    {action === "deposit" ? "Confirm Deposit" : "Confirm Withdrawal"}
                </DialogTitle>
                <DialogContent>
                    {action === "deposit" ? (
                        <>
                            <Typography>
                                You are about to create a deposit of <strong>{amount} EUR</strong> to <strong>{record?.name}</strong>.
                            </Typography>
                            <Box sx={{ mt: 2 }}>
                                <Typography>
                                    • Information about funding will be given subsequently. Pending deposits can be canceled anytime.
                                </Typography>
                            </Box>
                        </>
                    ) : (
                        <>
                            <Typography>
                                You are about to withdraw <strong>{amount} {amount === "1" ? "share" : "shares"}</strong> from <strong>{record?.name}</strong>.
                            </Typography>
                            <Box sx={{ mt: 2 }}>
                                <Typography>
                                    • The withdrawal will be processed at midnight UTC. The corresponding share price and statement will be assigned accordingly.
                                </Typography>
                            </Box>
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} color="secondary">Cancel</Button>
                    <Button onClick={handleConfirm} color="primary" variant="contained" disabled={status === "pending"}>
                        {status === "pending" ? "Processing..." : "Confirm"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
};
